import { Grid, Tooltip, useEventCallback } from '@mui/material';
import { Fragment, memo, SetStateAction } from 'react';
import { useFormik } from 'formik';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import { PmiButton } from 'shell/components/shared/button';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import { CitizenshipVM } from 'shell/view-models/citizenship';
import * as yup from 'yup';
import { DateTime } from 'luxon';
import { noop } from 'shell/utils/noop';

const validationSchema = yup.object().shape({
  state: yup.object().shape({
    value: yup.string().required('campo obbligatorio'),
  }),
  validity: yup.date().typeError('Formato non valido').nullable(),
  // dateControl: yup.date().nullable().notRequired().typeError('Formato non valido'),
  // .when('dateControl', (dateControl, formvalidationSchema) =>
  //   dateControl
  //     ? formvalidationSchema.min(
  //         dateControl,
  //         'La data deve essere maggiore o uguale della data decorrenza'
  //       )
  //     : formvalidationSchema.nullable()
  // ),
});

interface FormCitizenshipProps {
  isEditMode?: boolean;
  citizens?: Array<CitizenshipVM>;
  onAdd: (value: Array<CitizenshipVM>) => void;
  dateControl?: DateTime | null;
  numberMaxCitizenship?: number;
}

interface CitizenFormType {
  state?: ForeignStateVM | null;
  note?: string;
  validity?: DateTime | null;
}

const FormCitizenshipComponent = ({
  isEditMode,
  citizens,
  onAdd,
  numberMaxCitizenship = 0,
}: FormCitizenshipProps): React.ReactElement => {
  const onAddPress = useEventCallback(
    (
      citizen: CitizenFormType | Record<string, never>,
      setValues: (state: SetStateAction<CitizenFormType>) => void,
      isValid: boolean
    ) => {
      const citizenship = citizens?.slice() || [];

      const foundCitizen = citizenship.find(
        (citizeToFilter) => citizeToFilter.code === citizen.state?.id
      );

      const newCitizen: CitizenshipVM = {
        citizenship: citizen.state?.value || '',
        note: citizen.note || '',
        validityDate: citizen.validity?.toFormat('dd/MM/yyyy') || '',
        code: citizen.state?.id || '',
      };

      if (!foundCitizen && isValid) {
        citizenship.push(newCitizen);
        onAdd(citizenship);
        setValues({ ...citizen, state: undefined, note: '', validity: null });
      }
    }
  );

  const formContext = useFormik<CitizenFormType>({
    validationSchema,
    initialValues: {
      state: {
        id: '',
        province: '',
        value: '',
      },
      validity: null,
    },
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: noop,
  });

  return !isEditMode ? (
    <></>
  ) : (
    <Fragment>
      <Grid container spacing={2} item xs={12} md={6}>
        <Grid item xs={12}>
          <ForeignStateList
            value={formContext.values.state?.id ? formContext.values.state : null}
            onChange={(state) => {
              formContext.setFieldValue('state', state || null);
            }}
            label="Stato"
            id="state-nested-form"
            error={formContext.touched.state && Boolean(formContext.errors?.state)}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} item xs={12} md={6}>
        <Grid item xs={12}>
          <DatePicker
            label="Data validità"
            aria-label="Data validità"
            value={formContext.values.validity || null}
            onChange={(date) => {
              formContext.setFieldValue('validity', date);
            }}
            renderInput={(params) => (
              <PmiTextField
                id="validity"
                {...params}
                fullWidth
                error={Boolean(formContext.errors.validity)}
                helperText={formContext.errors.validity}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          label="Note"
          aria-label="Note"
          id="note"
          fullWidth
          multiline
          rows={2}
          inputProps={{
            maxLength: '240',
          }}
          value={formContext.values.note || ''}
          onChange={(event) => {
            formContext.setFieldValue('note', event?.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} display="flex" justifyContent="flex-end">
        <Tooltip
          arrow
          title={
            Boolean(numberMaxCitizenship) && (citizens || []).length >= numberMaxCitizenship
              ? 'Numero massimo di cittadinanza superato, eliminare per inserirne altre'
              : ''
          }
        >
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            disabled={
              !formContext.isValid ||
              (Boolean(numberMaxCitizenship) && (citizens || []).length >= numberMaxCitizenship)
            }
            onClick={() =>
              onAddPress(formContext.values, formContext.setValues, formContext.isValid)
            }
          >
            Inserisci Cittadinanza
          </PmiButton>
        </Tooltip>
      </Grid>
    </Fragment>
  );
};

FormCitizenshipComponent.displayName = 'Form Citizenship component';

const FormCitizenship = memo(FormCitizenshipComponent);

export default FormCitizenship;
